import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ym from 'react-yandex-metrika';
import { useLocation } from 'react-router';

import type { TeacherKPKListProps } from '@edu-frontend/modules/src/features/teacherKPKList';
import { useStaticUrls } from 'platform-components';
import { getExactPathMatch } from 'platform-components/utils';

import { teamBuilderActions } from 'common.components/team-builder/actions';
import { TEAM_BUILDER_ENTRY_STEPS } from 'common.components/team-builder/typings';

import { actions as teacherKPKActions } from '../store/actions';
import { teacherKPKsSelectors } from '../store/selectors';
import type { ExtendedTeacherKPK } from '../store/types';

import { useGoToClassroom } from './useGoToClassroom';

export const useTeacherKPKPageActions = () => {
    const dispatch = useDispatch();
    const goToClassroom = useGoToClassroom();

    const { pathname } = useLocation();
    const staticUrls = useStaticUrls();

    const actions: TeacherKPKListProps['actions'] = {
        onCreateClass: useCallback(() => {
            const { path = '' } = getExactPathMatch(staticUrls, pathname) || {};
            ym('reachGoal', 'click_add_class', { from: path });

            dispatch(
                teamBuilderActions.start({
                    entryPoint: TEAM_BUILDER_ENTRY_STEPS.TEAM,
                    onSuccess: () => dispatch(teacherKPKActions.getTeacherKPKsRequest()),
                })
            );
        }, [dispatch, pathname, staticUrls]),

        onContinueCourse: useCallback(
            ({ courseId }: ExtendedTeacherKPK) => {
                if (courseId) {
                    goToClassroom(courseId);
                }
            },
            [goToClassroom]
        ),

        onJoinButtonClick: useCallback(
            ({ courseTemplateId }: ExtendedTeacherKPK) => {
                ym('reachGoal', 'teacher_training_course_join', { template_id: courseTemplateId });

                dispatch(
                    teacherKPKActions.postTeacherKPKJoinRequest({
                        courseTemplateId: courseTemplateId,
                        onSuccess: goToClassroom,
                    })
                );
            },
            [dispatch, goToClassroom]
        ),
        joinLoading: useSelector(teacherKPKsSelectors.getPostTeacherKPKJoinLoading),

        onUpdateButtonClick: useCallback(
            ({ courseId }: ExtendedTeacherKPK) => {
                dispatch(
                    teacherKPKActions.postTeacherKPKsUpdateThreadRequest({
                        onSuccess: () => {
                            if (courseId) {
                                goToClassroom(courseId);
                            }
                        },
                        courseId,
                    })
                );
            },
            [dispatch, goToClassroom]
        ),
        updateLoading: useSelector(teacherKPKsSelectors.getPostTeacherKPKsUpdateThreadLoading),
    };

    return actions;
};
